import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Col, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers';
import { TypedMemo } from '../../common/types';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { useFollowArtist } from '../hooks/useFollowArtist';
import { ArtistsTable, ARTISTS_EXPORT_FILENAME } from '../components/ArtistsTable/ArtistsTable';
import { ExportCsvButton } from '../components/ExportCsvButton/ExportCsvButton';
import { ArtistSearchField, MeiliArtistData } from '../components/SearchField';
import { WatchlistSection } from './WatchlistSection';
import { WatchlistPagination } from './WatchlistPagination';
import { WATCHLIST_PAGE_SIZE } from './watchlistPageSize';

interface WatchlistArtistsPanelProps {
  selectedDate: Date;
  selectedDateValue: string;
}

/** The followed artists, with search-to-follow and CSV export. */
const WatchlistArtistsPanelComponent: React.FC<WatchlistArtistsPanelProps> = ({ selectedDate, selectedDateValue }) => {
  const intl = useIntl();
  const trpcMusicAPI = useTRPCAPI();
  const { showSuccess, showError } = useShowToast();
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useTRPCQuery(
    trpcMusicAPI.artists.listFollowed.queryOptions({
      offset: (page - 1) * WATCHLIST_PAGE_SIZE,
      limit: WATCHLIST_PAGE_SIZE,
    })
  );

  const { followArtist, isPending } = useFollowArtist({
    onSuccess: () => {
      showSuccess(
        intl.formatMessage({
          id: 'app.sml.artists.follow.success',
          defaultMessage: 'Artist added to your followed list',
        })
      );
    },
    onError: (followError) => {
      showError(
        followError.message ||
          intl.formatMessage({ id: 'app.sml.artists.follow.error', defaultMessage: 'Failed to follow artist' })
      );
    },
  });

  const onSelected = useCallback((artist: MeiliArtistData) => followArtist(artist.id), [followArtist]);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  return (
    <WatchlistSection
      icon={<FontAwesomeIcon icon={faUsers} />}
      title={intl.formatMessage({ id: 'app.sml.home.tab.artists', defaultMessage: 'Artists' })}
      exportButton={
        <ExportCsvButton kind="artists" filename={ARTISTS_EXPORT_FILENAME} isEmpty={(data?.total ?? 0) === 0} />
      }
      search={
        <Row className="mb-3">
          <Col xs={12} md={6} lg={5}>
            <ArtistSearchField
              placeholder={intl.formatMessage({
                id: 'app.search.artists.placeholder',
                defaultMessage: 'Search for artists...',
              })}
              onArtistSelected={onSelected}
              isDisabled={isPending}
            />
          </Col>
        </Row>
      }
      error={error}
      retry={retry}
    >
      <ArtistsTable
        artists={data?.artists ?? []}
        isLoading={isLoading}
        selectedDateValue={selectedDateValue}
        selectedDate={selectedDate}
      />
      <WatchlistPagination
        page={page}
        onPageChange={setPage}
        hasNext={data?.hasMore === true}
        total={data?.total ?? 0}
        entityLabel={intl.formatMessage({ id: 'app.sml.home.entity.artists', defaultMessage: 'artists' })}
      />
    </WatchlistSection>
  );
};

export const WatchlistArtistsPanel = TypedMemo(WatchlistArtistsPanelComponent);
